import { Brand, type BrandFeature } from "@src/domain/entities/brand";

export function isActiveBrandFeature(feature: BrandFeature): boolean {
  return Boolean(
    feature.brandFeatureIsActive && feature.brandFeatureMappingId != null,
  );
}

export function countActiveBrandFeatures(
  features: BrandFeature[] | null | undefined,
): number {
  return (features ?? []).filter(isActiveBrandFeature).length;
}

export function mergeBrandFeaturesAfterStatusUpdate(
  features: BrandFeature[],
  updatedFeature: BrandFeature,
  featureKey: string,
): BrandFeature[] {
  const keysToEvict = new Set(
    [featureKey, updatedFeature.brandFeatureKey].filter(Boolean),
  );

  if (keysToEvict.size > 0) {
    return [
      ...features.filter((f) => !keysToEvict.has(f.brandFeatureKey)),
      updatedFeature,
    ];
  }

  let existingIndex = features.findIndex(
    (f) => f.brandFeatureId === updatedFeature.brandFeatureId,
  );
  if (existingIndex < 0) {
    existingIndex = features.findIndex(
      (f) =>
        f.brandFeatureMappingId === updatedFeature.brandFeatureMappingId,
    );
  }
  if (existingIndex >= 0) {
    return features.map((f, i) =>
      i === existingIndex ? updatedFeature : f,
    );
  }
  return [...features, updatedFeature];
}

export function applyBrandFeatureStatusToBrand(
  brand: Brand,
  updatedFeature: BrandFeature,
  featureKey: string,
): Brand {
  const nextFeatures = mergeBrandFeaturesAfterStatusUpdate(
    brand.brandFeatures ?? [],
    updatedFeature,
    featureKey,
  );
  return Brand.withProps(brand, {
    brandFeatures: nextFeatures,
    brandFeaturesCount: countActiveBrandFeatures(nextFeatures),
  });
}
